import { api } from "@services/api"
import {
  getTenantService,
  getAllTenantsService,
  updateTenantService,
  sendEmailService,
  sendGroupEmailsService,
  sendOrdersEmailService,
  sendPasswordResetEmailService,
  getAppMailsService,
  updateAppMailService,
  sendAppNotificationService,
  getAppNotificationService,
  countGroupPushRecipientsService,
  sendGroupPushNotificationsService,
  getAnalyticsService,
  addExpenseService,
  sendWebHookService,
} from "./admin-service"

interface DataItem {
  monthStart: string
  monthEnd: string
  newUsers: string
  vehicles: string
  ordersQty: string
  ordersIncome: number
  grossAmt: number
  expensesAmt: number
}

const transform = (responseData: any) => {
  // Backend now returns { data: [...], totals: {...} }
  const { data, totals } = responseData

  const transformedData = data.map((obj: DataItem) => ({
    grossProfit: obj.ordersIncome - obj.grossAmt,
    realisedProfit: obj.ordersIncome - obj.expensesAmt,
    ...obj,
  }))

  return {
    data: transformedData,
    totals: totals,
  }
}

export const tenantsApi = api.injectEndpoints({
  endpoints: (build) => ({
    //get all tenants
    getAllTenants: build.query({
      queryFn: async () => {
        const result: any = await new Promise((resolve) =>
          getAllTenantsService()
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
      providesTags: ["Tenants"],
    }),

    //get a tenant
    getTenant: build.query({
      queryFn: async ({ apikey, tenantid }) => {
        const result: any = await new Promise((resolve) =>
          getTenantService(apikey, tenantid)
            .then((data: unknown) => resolve(data))
            .catch((error: { response: { data: any } }) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
      providesTags: ["Tenants"],
    }),

    //update a tenant
    updateTenant: build.mutation({
      queryFn: async ({ data, tenantid }) => {
        const result: any = await new Promise((resolve) =>
          updateTenantService(data, tenantid)
            .then((data: unknown) => resolve(data))
            .catch((error: { response: { data: any } }) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
      invalidatesTags: ["Tenants"],
    }),

    // send emails
    sendEmail: build.mutation({
      queryFn: async (body) => {
        const result: any = await new Promise((resolve) =>
          sendEmailService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
      invalidatesTags: ["Email"],
    }),

    // send group emails
    sendGroupEmails: build.mutation({
      queryFn: async (body) => {
        const result: any = await new Promise((resolve) =>
          sendGroupEmailsService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
      // invalidatesTags: ["Email"],
    }),

    // send orders email
    sendOrdersEmail: build.mutation({
      queryFn: async (body) => {
        const result: any = await new Promise((resolve) =>
          sendOrdersEmailService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
      // invalidatesTags: ["Email"],
    }),

    // send orders email
    sendPasswordResetEmail: build.mutation({
      queryFn: async (body) => {
        const result: any = await new Promise((resolve) =>
          sendPasswordResetEmailService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
      // invalidatesTags: ["Email"],
    }),

    // count group push recipients
    countGroupPushRecipients: build.query({
      queryFn: async (body) => {
        const result: any = await new Promise((resolve) =>
          countGroupPushRecipientsService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
    }),

    // send group push notifications
    sendGroupPushNotifications: build.mutation({
      queryFn: async (body) => {
        const result: any = await new Promise((resolve) =>
          sendGroupPushNotificationsService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
    }),

    // send app notification
    sendAppNotification: build.mutation({
      queryFn: async (body) => {
        const result: any = await new Promise((resolve) =>
          sendAppNotificationService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
      invalidatesTags: ["AppNotifications"],
    }),

    // get app notifications
    getAppNotifications: build.query({
      queryFn: async ({ type, seen, userCreatedAt }) => {
        const result: any = await new Promise((resolve) =>
          getAppNotificationService(type, seen, userCreatedAt)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
      providesTags: ["AppNotifications"],
    }),

    // get app mails/ messages
    getAppMails: build.query({
      queryFn: async ({ mssgType }) => {
        const result: any = await new Promise((resolve) =>
          getAppMailsService(mssgType)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
      providesTags: ["AppMails"],
    }),

    // update app mails/messages
    updateAppMail: build.mutation({
      queryFn: async (body) => {
        const result: any = await new Promise((resolve) =>
          updateAppMailService(body)
            .then((data) => resolve(data))
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
      invalidatesTags: ["AppMails"],
    }),

    //get admin analytics
    getAnalytics: build.query({
      queryFn: async ({
        start,
        end,
        previousStart,
        previousEnd,
        previousTwoStart,
        previousTwoEnd,
        previousThreeStart,
        previousThreeEnd,
        filterType,
        days,
        type = "admin",
      }) => {
        const result: any = await new Promise((resolve) =>
          getAnalyticsService({
            start,
            end,
            previousStart,
            previousEnd,
            previousTwoStart,
            previousTwoEnd,
            previousThreeStart,
            previousThreeEnd,
            filterType,
            days,
            type,
          })
            .then((data) => {
              const res = type === "admin" ? transform(data) : data
              resolve(res)
            })
            .catch((error) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )

        return { data: result, error: result.error }
      },
      providesTags: ["Analytics"],
    }),

    // add expense
    addExpense: build.mutation({
      queryFn: async (data) => {
        const result: any = await new Promise((resolve) =>
          addExpenseService(data)
            .then((data: unknown) => resolve(data))
            .catch((error: { response: { data: any } }) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )
        return { data: result, error: result?.error ? result : undefined }
      },
      invalidatesTags: ["Expenses"],
    }),

    // send webhook
    sendWebHook: build.mutation({
      queryFn: async (data) => {
        const result: any = await new Promise((resolve) =>
          sendWebHookService(data)
            .then((data: unknown) => resolve(data))
            .catch((error: { response: { data: any } }) => {
              if (error) {
                resolve({ data: null, error: error.response.data })
              }
            })
        )
        return { data: result, error: result?.error ? result : undefined }
      },
    }),
  }),
})

export const {
  useGetTenantQuery,
  useGetAllTenantsQuery,
  useSendEmailMutation,
  useSendGroupEmailsMutation,
  useSendOrdersEmailMutation,
  useSendPasswordResetEmailMutation,
  useSendAppNotificationMutation,
  useGetAppNotificationsQuery,
  useCountGroupPushRecipientsQuery,
  useSendGroupPushNotificationsMutation,
  useGetAppMailsQuery,
  useUpdateAppMailMutation,
  useUpdateTenantMutation,
  useGetAnalyticsQuery,
  useAddExpenseMutation,
  useSendWebHookMutation,
} = tenantsApi
